import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { getISOWeek } from 'date-fns';
import { t } from 'i18next';
import CalendarPicker from '../util/CalendarPicker';

interface Props {
  userId: number;
}

// История TOIL сотрудника — та же вёрстка, что «full history» в Timesheet → TOIL (ToilCalendar).
const UserToilHistory: React.FC<Props> = ({ userId }) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [openComment, setOpenComment] = useState<string | number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`toils/user/${userId}`);
      setData((Array.isArray(d) ? d : []).sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime()));
    } catch { /* глобальный interceptor */ } finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => data.filter(entry => {
    const d = entry.date?.slice(0, 10) ?? '';
    if (from && d < from) return false;
    if (to && d > to) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!d.includes(q) && !String(entry.source ?? '').toLowerCase().includes(q)) return false;
    }
    return true;
  }), [data, from, to, search]);

  const balance = useMemo(() => filtered.reduce((s: number, e: any) => s + (e.total_sum ?? 0), 0), [filtered]);

  // Running balance: compute cumulative sum per entry (oldest → newest), attach to each entry
  const filteredWithBalance = useMemo(() => {
    // Sort ascending for cumulative calc, then reverse back
    const asc = [...filtered].sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
    let running = 0;
    const withBal = asc.map((e: any) => { running += (e.total_sum ?? 0); return { ...e, _balance: Math.round(running * 100) / 100 }; });
    return withBal.reverse();
  }, [filtered]);

  const th: React.CSSProperties = { padding: '8px 8px', textAlign: 'left', fontSize: 10, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', borderBottom: '1px solid var(--border-input)', whiteSpace: 'nowrap' };
  const inp: React.CSSProperties = { height: 28, background: 'var(--bg-input, var(--btn-user-form))', border: '1px solid var(--border-input)', borderRadius: 7, color: 'var(--text-color)', fontSize: 12, padding: '0 8px', outline: 'none' };
  const lbl: React.CSSProperties = { fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text-color-calendar-week)', marginBottom: 4 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Balance summary */}
      <div style={{ padding: '12px 4px', borderBottom: '1px solid var(--border-input)', display: 'flex', gap: 24 }}>
        <div>
          <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', marginBottom: 3 }}>{t('NetToilFiltered') || 'Net TOIL (filtered)'}</div>
          <div style={{ fontSize: 22, fontWeight: 900, fontVariantNumeric: 'tabular-nums', color: balance >= 0 ? '#059669' : '#ef4444' }}>
            {balance >= 0 ? '+' : ''}{Math.round(balance * 100) / 100}h
          </div>
        </div>
        <div>
          <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', marginBottom: 3 }}>{t('Entries') || 'Entries'}</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--text-color)' }}>{filtered.length}</div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ padding: '10px 4px', borderBottom: '1px solid var(--border-input)', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: 1, minWidth: 120 }}>
          <div style={lbl}>{t('Search') || 'Search'}</div>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('DateOrSource') || 'date or source...'}
            style={{ ...inp, width: '100%', height: 28, padding: '0 9px' }} />
        </div>
        <div>
          <div style={lbl}>{t('From') || 'From'}</div>
          <CalendarPicker mode="single" variant="field" output="string" value={from} onChange={(v) => setFrom(v)} />
        </div>
        <div>
          <div style={lbl}>{t('To') || 'To'}</div>
          <CalendarPicker mode="single" variant="field" output="string" value={to} onChange={(v) => setTo(v)} />
        </div>
        {(from || to || search) && (
          <button type="button" onClick={() => { setSearch(''); setFrom(''); setTo(''); }}
            style={{ height: 28, padding: '0 10px', borderRadius: 7, border: '1px solid var(--border-input)', background: 'var(--btn-user-form)', color: 'var(--text-color-calendar-week)', fontSize: 11, cursor: 'pointer' }}>
            {t('Clear') || 'Clear'}
          </button>
        )}
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-color-calendar-week)', fontSize: 13 }}>{t('Loading') || 'Loading…'}</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-color-calendar-week)', fontSize: 13 }}>{t('Empty') || 'No entries'}</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--btn-user-form)' }}>
                <th style={{ ...th, padding: '8px 14px' }}>{t('Date')}</th>
                <th style={th}>{t('Week') || 'Week'}</th>
                <th style={th}>{t('HrSource') || 'Source'}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('Logged') || 'Logged'}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('Contract') || 'Contract'}</th>
                <th style={{ ...th, textAlign: 'right' }}>TOIL</th>
                <th style={{ ...th, textAlign: 'right', padding: '8px 14px 8px 0' }}>{t('Balance') || 'Balance'}</th>
              </tr>
            </thead>
            <tbody>
              {filteredWithBalance.map((entry: any, i: number) => {
                const delta = entry.total_sum ?? 0;
                const isReport = entry.source === 'report';
                const logged = entry.total_hours != null ? `${entry.total_hours}h` : '—';
                const contract = entry.hours_per_week != null ? `${entry.hours_per_week}h` : '—';
                const rowKey = entry.id ?? i;
                const comment = (entry.description ?? '').trim();
                const commentOpen = openComment === rowKey;
                return (
                  <React.Fragment key={rowKey}>
                    <tr style={{ borderBottom: comment && commentOpen ? 'none' : '1px solid var(--border-input)' }}>
                      <td style={{ padding: '9px 14px', fontSize: 13, color: 'var(--text-color)', fontVariantNumeric: 'tabular-nums', fontWeight: 500, whiteSpace: 'nowrap' }}>{entry.date?.slice(0, 10) ?? '—'}</td>
                      <td style={{ padding: '9px 8px', fontSize: 12, color: 'var(--text-color-calendar-week)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{entry.date ? `W${getISOWeek(new Date(entry.date))}` : '—'}</td>
                      <td style={{ padding: '9px 8px', fontSize: 12, color: 'var(--text-color-calendar-week)' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ padding: '2px 7px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: isReport ? 'rgba(37,99,235,.1)' : 'rgba(124,58,237,.1)', color: isReport ? '#2563eb' : '#7c3aed' }}>
                            {isReport ? (t('Report') || 'Report') : (t('Manual') || 'Manual')}
                          </span>
                          {comment && (
                            <button type="button" onClick={() => setOpenComment(prev => prev === rowKey ? null : rowKey)} title={t('Comment') as string} aria-label="Comment"
                              style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: 0, opacity: commentOpen ? 1 : .55 }}>💬</button>
                          )}
                        </span>
                      </td>
                      <td style={{ padding: '9px 8px', textAlign: 'right', fontSize: 13, fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: isReport ? 'var(--text-color)' : 'var(--text-color-calendar-week)' }}>{isReport ? logged : '—'}</td>
                      <td style={{ padding: '9px 8px', textAlign: 'right', fontSize: 13, fontVariantNumeric: 'tabular-nums', color: 'var(--text-color-calendar-week)' }}>{isReport ? contract : '—'}</td>
                      <td style={{ padding: '9px 8px', textAlign: 'right', fontSize: 14, fontWeight: 900, fontVariantNumeric: 'tabular-nums', color: delta > 0 ? '#059669' : delta < 0 ? '#ef4444' : 'var(--text-color-calendar-week)' }}>{delta > 0 ? '+' : ''}{delta}h</td>
                      <td style={{ padding: '9px 14px 9px 0', textAlign: 'right', fontSize: 12, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: entry._balance > 0 ? '#059669' : entry._balance < 0 ? '#ef4444' : 'var(--text-color-calendar-week)', opacity: .8 }}>{entry._balance > 0 ? '+' : ''}{entry._balance}h</td>
                    </tr>
                    {comment && commentOpen && (
                      <tr style={{ borderBottom: '1px solid var(--border-input)' }}>
                        <td colSpan={7} style={{ padding: '2px 14px 10px 14px' }}>
                          <div style={{ background: 'var(--btn-user-form)', border: '1px solid var(--border-input)', borderRadius: 8, padding: '8px 12px', fontSize: 12.5, color: 'var(--text-color)', whiteSpace: 'pre-wrap' }}>{comment}</div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default UserToilHistory;
